// ============================================================
//  src/pages/ProductDetailPage.jsx — React Router v6 migration
// ============================================================

import { useState, useCallback, useEffect } from "react";   // ← added useEffect
import { useNavigate, useParams } from "react-router-dom";
import { Toast } from "../components/UI";
import ProductImage from "../components/ProductImage";
import { addItemToCart } from "../utils/helpers";
import useProducts from "../hooks/useProducts";
import { supabase } from "../utils/supabase";               // ← added supabase
import config from "../config";


export default function ProductDetailPage({ setCart }) {
  const navigate          = useNavigate();
  const { productId }     = useParams();
  const { products }      = useProducts();
  const product           = products.find((p) => String(p.id) === String(productId));

  const [selectedSize, setSelectedSize] = useState(null);
  const [activeTab, setActiveTab]       = useState("highlights");
  const [toast, setToast]               = useState(null);
  const [copied, setCopied]             = useState(false);
  const [stockInfo, setStockInfo]       = useState(null);   // ← moved up before any hooks calls

  // Fetch stock info + subscribe to realtime updates
  useEffect(() => {
    if (!productId) return;

    // 1. Initial fetch — populate immediately on page load
    supabase
      .from('product_stock')
      .select('status, quantity')
      .eq('product_id', String(productId))
      .maybeSingle()
      .then(({ data }) => setStockInfo(data));

    // 2. Realtime subscription — reflects any UPDATE pushed from Supabase instantly
    const channel = supabase
      .channel(`stock-${productId}`)
      .on(
        'postgres_changes',
        {
          event:  'UPDATE',
          schema: 'public',
          table:  'product_stock',
          filter: `product_id=eq.${productId}`,
        },
        (payload) => setStockInfo(payload.new)
      )
      .subscribe();

    // 3. Cleanup — unsubscribe when component unmounts or productId changes
    return () => { supabase.removeChannel(channel); };
  }, [productId]);

  // selectedSize defaults to first size once product loads
  const currentSize = selectedSize ?? product?.sizes?.[0] ?? product?.weight;

  const handleAddToCart = useCallback(() => {
    if (!product) return;
    const selectedPrice = product.prices?.[currentSize] ?? product.price;
    setCart((prev) => addItemToCart(prev, { ...product, selectedSize: currentSize, price: selectedPrice }));
    setToast(`${product.name} (${currentSize}) added to cart!`);
  }, [product, currentSize, setCart]);

  const handleBuyNow = useCallback(() => {
    if (!product) return;
    const selectedPrice = product.prices?.[currentSize] ?? product.price;
    setCart((prev) => addItemToCart(prev, { ...product, selectedSize: currentSize, price: selectedPrice }));
    navigate("/cart");
  }, [product, currentSize, setCart, navigate]);

  function copyHighlights() {
    const text = product?.highlights?.join("\n") || "";
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); });
  }

  if (!product) return (
    <div style={{ textAlign: "center", padding: "80px 20px", color: "#aaa" }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🌾</div>
      <div style={{ fontSize: 16, fontWeight: 700 }}>Product not found</div>
      <button onClick={() => navigate("/")} style={{ marginTop: 20, background: config.colors.primary, color: "#fff", border: "none", borderRadius: 24, padding: "10px 28px", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>← Back to Shop</button>
    </div>
  );

  const { name, emoji, color, price, rating, reviews, badge, category, origin, description, vegNonVeg, shelfLife, netQuantity, storageInstructions, highlights, nutritionPer100g, sizes } = product;
  const selectedPrice = product.prices?.[currentSize] ?? product.price;

  const stockStatus = stockInfo?.status ?? 'in_stock';
  const stockBadge = {
    in_stock:     { label: '✅ In Stock',     color: '#27ae60' },
    low_stock:    { label: '⚠️ Low Stock',    color: '#c2410c' },
    out_of_stock: { label: '❌ Out of Stock', color: '#dc2626' },
  }[stockStatus] ?? { label: '✅ In Stock', color: '#27ae60' };

  const isOutOfStock = stockStatus === "out_of_stock";

  return (
    <div style={{ background: config.colors.background, minHeight: "100vh", paddingBottom: 100 }}>

      <div style={{ padding: "12px 14px 0" }}>
        <button className="btn-press" onClick={() => navigate(-1)}
          style={{ background: "#fff", border: `1px solid ${config.colors.cardBorder}`, borderRadius: 20, padding: "6px 14px", fontSize: 13, fontWeight: 700, color: config.colors.primary, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          ← Back to Shop
        </button>
      </div>

      <div style={{ padding: "14px 14px 0" }}>
        <div style={{ borderRadius: 18, height: 280, position: "relative", overflow: "hidden", border: `1px solid ${color}33` }}>
          <span style={{ position: "absolute", top: 14, left: 14, zIndex: 2, background: config.colors.primary, color: "#fff", fontSize: 11, fontWeight: 800, padding: "4px 12px", borderRadius: 20, letterSpacing: 0.5 }}>{badge}</span>
          <span style={{ position: "absolute", top: 14, right: 14, zIndex: 2, background: "#fff", borderRadius: 8, padding: "4px 8px", display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "#555", border: `1px solid ${config.colors.cardBorder}` }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: vegNonVeg === "Veg" ? "#27ae60" : "#e74c3c", display: "inline-block", flexShrink: 0 }} />{vegNonVeg}
          </span>
          <ProductImage src={product.image} emoji={emoji} alt={name} color={color} height={280} fontSize={110} imgStyle={{ objectFit: "contain", padding: 12 }} />
        </div>
      </div>

      <div style={{ padding: "16px 14px 0" }}>
        <div style={{ fontSize: 12, color: config.colors.textMuted, marginBottom: 6 }}>{config.storeName} &nbsp;›&nbsp; {category}</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(18px, 5vw, 24px)", fontWeight: 800, color: config.colors.text, lineHeight: 1.3, marginBottom: 10 }}>{name}</h1>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          <div style={{ background: config.colors.primary, color: "#fff", borderRadius: 8, padding: "3px 10px", fontSize: 13, fontWeight: 800, display: "flex", alignItems: "center", gap: 4 }}>⭐ {rating}</div>
          <span style={{ fontSize: 13, color: config.colors.textMuted }}>{reviews} Reviews</span>
          <span style={{ color: config.colors.cardBorder }}>|</span>
          <span style={{ fontSize: 12, color: stockBadge.color, fontWeight: 700 }}>
            {stockBadge.label}
            {stockInfo?.quantity ? ` · ${stockInfo.quantity} units` : ''}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <div style={{ fontFamily: "Domine", fontSize: 32, fontWeight: 900, color: config.colors.text }}>₹{selectedPrice}</div>
        </div>
        <div style={{ fontSize: 12, color: config.colors.textMuted, marginBottom: 20 }}>📍 Origin: {origin}</div>

        {sizes && sizes.length > 0 && (
          <div style={{ background: "#fff", borderRadius: 14, padding: "16px 14px", border: `1px solid ${config.colors.cardBorder}`, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", marginBottom: 14 }}>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 12 }}>Select Size</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {sizes.map((s) => (
                <button key={s} className="btn-press" onClick={() => setSelectedSize(s)}
                  style={{ background: currentSize === s ? config.colors.primary : "#fff", color: currentSize === s ? "#fff" : config.colors.primary, border: `2px solid ${currentSize === s ? config.colors.primary : config.colors.cardBorder}`, borderRadius: 20, padding: "8px 20px", fontWeight: 700, fontSize: 13, cursor: "pointer", transition: "all 0.18s" }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ background: "#fff", borderRadius: 14, padding: "16px 14px", border: `1px solid ${config.colors.cardBorder}`, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontWeight: 800, fontSize: 15 }}>Product Highlights</div>
            <button className="btn-press" onClick={copyHighlights} style={{ background: "none", border: "none", cursor: "pointer", color: config.colors.primary, fontWeight: 800, fontSize: 12, padding: "4px 8px" }}>{copied ? "✓ COPIED" : "COPY"}</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10, marginBottom: 16 }}>
            {highlights?.map((h) => (
              <div key={h} style={{ background: config.colors.background, borderRadius: 10, padding: "9px 12px", fontSize: 13, color: "#444", fontWeight: 600, display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ color: config.colors.primary, fontWeight: 800, flexShrink: 0 }}>✓</span>{h}
              </div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, borderTop: `1px solid ${config.colors.cardBorder}`, paddingTop: 14 }}>
            <InfoCell label="Net Quantity (N)"          value={netQuantity} />
            <InfoCell label="Shelf Life (Best Before)"  value={shelfLife} />
            <InfoCell label="Veg / NonVeg"              value={vegNonVeg} />
            <InfoCell label="Seller FSSAI License"      value={config.fssaiLicense} mono />
          </div>
        </div>

        <div style={{ background: "#fff", borderRadius: 14, border: `1px solid ${config.colors.cardBorder}`, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", marginBottom: 14, overflow: "hidden" }}>
          <div style={{ display: "flex", borderBottom: `1px solid ${config.colors.cardBorder}` }}>
            {[{ id: "highlights", label: "Description" }, { id: "nutrition", label: "Nutrition" }, { id: "storage", label: "Storage" }].map((t) => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ flex: 1, background: "none", border: "none", cursor: "pointer", padding: "12px 6px", fontSize: 12, fontWeight: 800, color: activeTab === t.id ? config.colors.primary : "#bbb", borderBottom: activeTab === t.id ? `2px solid ${config.colors.primary}` : "2px solid transparent", transition: "all 0.18s" }}>{t.label}</button>
            ))}
          </div>
          <div style={{ padding: "16px 14px" }}>
            {activeTab === "highlights" && <p style={{ fontSize: 14, color: "#555", lineHeight: 1.8 }}>{description}</p>}
            {activeTab === "nutrition" && (
              <div>
                <div style={{ fontWeight: 700, fontSize: 12, color: config.colors.textMuted, marginBottom: 10 }}>Nutritional Information (Per 100g)</div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <tbody>
                    {Object.entries(nutritionPer100g || {}).map(([key, val], i) => (
                      <tr key={key} style={{ background: i % 2 === 0 ? config.colors.background : "#fff" }}>
                        <td style={{ padding: "8px 10px", fontSize: 13, color: "#555", fontWeight: 600 }}>{key}</td>
                        <td style={{ padding: "8px 10px", fontSize: 13, fontWeight: 700, color: config.colors.text, textAlign: "right" }}>{val}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {activeTab === "storage" && (
              <div>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 28, flexShrink: 0 }}>📦</span>
                  <p style={{ fontSize: 14, color: "#555", lineHeight: 1.8 }}>{storageInstructions}</p>
                </div>
                <div style={{ marginTop: 14, background: "#fff3e0", borderRadius: 10, padding: "10px 12px", fontSize: 12, color: "#e65100", fontWeight: 600 }}>💡 Best consumed within {shelfLife} of manufacture date.</div>
              </div>
            )}
          </div>
        </div>

        <div style={{ background: "#fff", borderRadius: 14, padding: "16px 14px", border: `1px solid ${config.colors.cardBorder}`, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", marginBottom: 14 }}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 12 }}>Additional Details</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <DetailRow label="Brand"             value={config.storeName} />
            <DetailRow label="Origin"            value={origin} />
            <DetailRow label="Category"          value={category} />
            <DetailRow label="Available Sizes"   value={sizes?.join(", ")} />
            <DetailRow label="GST Number"        value={config.gstNumber} mono />
            <DetailRow label="FSSAI License No." value={config.fssaiLicense} mono />
            <DetailRow label="FSSAI Valid Upto"  value={config.fssaiValidUpto} />
          </div>
        </div>

        <div style={{ background: `linear-gradient(135deg, ${config.colors.primary}11, ${config.colors.primaryLight}22)`, borderRadius: 14, padding: "14px", border: `1px solid ${config.colors.primary}33`, marginBottom: 14, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 44, height: 44, minWidth: 44, background: `linear-gradient(135deg, ${config.colors.primary}, ${config.colors.primaryLight})`, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🌾</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, color: config.colors.text }}>{config.storeName}</div>
            <div style={{ fontSize: 11, color: "#777", marginTop: 2 }}>✅ Verified Supplier &nbsp;·&nbsp; ⭐ {config.rating} Rating</div>
            <div style={{ fontSize: 11, color: config.colors.primary, fontWeight: 700, marginTop: 2 }}>FSSAI: {config.fssaiLicense}</div>
          </div>
        </div>
      </div>

      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "#fff", borderTop: `2px solid ${config.colors.cardBorder}`, padding: "10px 14px 14px", display: "flex", gap: 12, zIndex: 190, boxShadow: "0 -4px 20px rgba(0,0,0,0.10)" }}>
        <button
          className="btn-press"
          onClick={handleAddToCart}
          disabled={isOutOfStock}
          style={{ flex: 1, background: isOutOfStock ? "#f0f0f0" : "#fff", color: isOutOfStock ? "#bbb" : config.colors.primary, border: `2px solid ${isOutOfStock ? "#ddd" : config.colors.primary}`, borderRadius: 13, padding: "14px 0", fontWeight: 800, fontSize: 14, cursor: isOutOfStock ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
        >
          🛒 Add to Cart
        </button>
        <button
          className="btn-press"
          onClick={handleBuyNow}
          disabled={isOutOfStock}
          style={{ flex: 1, background: isOutOfStock ? "#f0f0f0" : config.colors.accent, color: isOutOfStock ? "#bbb" : "#fff", border: "none", borderRadius: 13, padding: "14px 0", fontWeight: 800, fontSize: 14, cursor: isOutOfStock ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
        >
          ⚡ Buy Now
        </button>
      </div>

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}

function InfoCell({ label, value, mono = false }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: "#bbb", fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: config.colors.text, fontFamily: mono ? "'Courier New', monospace" : "inherit", wordBreak: "break-all" }}>{value}</div>
    </div>
  );
}

function DetailRow({ label, value, mono = false }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, paddingBottom: 10, borderBottom: `1px solid ${config.colors.cardBorder}` }}>
      <span style={{ fontSize: 13, color: config.colors.textMuted, fontWeight: 600, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: config.colors.text, textAlign: "right", wordBreak: "break-all", fontFamily: mono ? "'Courier New', monospace" : "inherit" }}>{value}</span>
    </div>
  );
}
